import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';

/**
 * Interface estandarizada para las opciones de consultas paginadas y filtradas.
 */
export interface PagedRequestOptions {
  page?: number;
  limit?: number;
  clientId?: number;
  searchTerm?: string;
  serviceType?: string;
  tipoSolicitud?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC' | string;
  export?: boolean;
  // Filtros de fecha
  fechaInicio?: string;
  fechaFin?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  // Filtros de estado y clasificación
  status?: string;
  tipoMantenimiento?: string;  
  mesFacturacion?: string;
  // Identificador de usuario para aplicar filtros RBAC y locales
  userId?: number;
  localIds?: string;
  activoFijoId?: number;
  // Dashboard Dinámico
  dias?: number | null;
  kpiType?: string;
  statusTarget?: string;
  cumplimientoType?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SolicitarVisitaService {
  private apiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient, 
    private authService: AuthService
  ) { }

  // ==========================================================================
  // 1. CONSULTAS PAGINADAS ESTANDARIZADAS (BANDEJAS)
  // ==========================================================================

  /** Obtiene las solicitudes en estado Pendiente (Paginado) */
  getPendientesPaginado(options: PagedRequestOptions): Observable<any> {
    return this.fetchPaginatedData('/pendientes', options);
  }

  /** Obtiene las solicitudes en estado Aprobada (Paginado) */
  getSolicitudesAprobadasPaginado(options: PagedRequestOptions): Observable<any> {
    return this.fetchPaginatedData('/aprobadas', options);
  }

  /** Obtiene las solicitudes en estado Rechazada (Paginado) */
  getSolicitudesRechazadasPaginado(options: PagedRequestOptions): Observable<any> {
    return this.fetchPaginatedData('/rechazadas', options);
  }

  /** Obtiene las solicitudes en estado Atendida en Proceso / En Proceso (Paginado) */
  getSolicitudesAtendidasEnProcesoPaginado(options: PagedRequestOptions): Observable<any> {
    return this.fetchPaginatedData('/atendidas_proceso', options);
  }

  /** Obtiene las solicitudes en estado Finalizada (Paginado) */
  getSolicitudesFinalizadasPaginado(options: PagedRequestOptions): Observable<any> {
    return this.fetchPaginatedData('/finalizadas', options);
  }

  /** Obtiene las solicitudes en estado Validada (Paginado) */
  getSolicitudesValidadasPaginado(options: PagedRequestOptions): Observable<any> {
    return this.fetchPaginatedData('/validadas', options);
  }

  getSolicitudesProgramadasPaginado(options: PagedRequestOptions): Observable<any> {
    return this.fetchPaginatedData('/programadas', options);
  }

  /** Obtiene las solicitudes correspondientes al día actual (Paginado) */
  getSolicitudesDelDiaPaginado(options: PagedRequestOptions): Observable<any> {
    return this.fetchPaginatedData('/solicitudes-del-dia', options);
  }

  /** Obtiene solicitudes utilizando múltiples filtros combinados (Paginado) */
  getSolicitudesVisitaMultifiltro(options: PagedRequestOptions): Observable<any> {
    return this.fetchPaginatedData('/solicitudes-visita-multifiltro', options);
  }

  // ==========================================================================
  // 2. MÁQUINA DE ESTADOS Y TRANSICIONES (RBAC APLICADO)
  // ==========================================================================

  /** Transiciona una solicitud a estado APROBADA */
  aprobarSolicitudVisita(id: number, data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/solicitar-visita/${id}/aprobar`, data);
  }
  
  /** Transiciona una solicitud a estado RECHAZADA */
  rechazarSolicitudVisita(id: number, data: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/solicitar-visita/${id}/rechazar`, data);
  }

  /** Transiciona una solicitud a estado PROGRAMADA asignando fecha y técnico */
  programarSolicitud(id: number, data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/solicitar-visita/${id}/programar`, data);
  }

  /** Transiciona una solicitud a estado EN PROCESO (Pausada por algún motivo) */
  dejarEnProceso(id: number, data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/solicitar-visita/${id}/en-proceso`, data);
  }

  /** Transiciona una solicitud a estado VALIDADA (Aprobación final) */
  validarSolicitud(id: number, data: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/solicitar-visita/${id}/validar`, data);
  }

  /** Devuelve una solicitud finalizada/validada nuevamente al flujo activo */
  reabrirSolicitud(id: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/solicitar-visita/${id}/reabrir`, {});
  }

  /** Forzar un cambio de estado arbitrario (Usar con precaución) */
  cambiarEstado(id: number, estado: string, observacion?: string): Observable<any> {
    return this.http.patch(`${this.apiUrl}/solicitar-visita/${id}/estado`, { status: estado, observacion });
  }

  // Método para invocar el arrastre automático de tickets no preventivos (CR-202608-0035)
  arrastrarTicketsNoPreventivos(preventivoId: number, tecnicoId: number, fechaVisitaPreventivo: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/solicitar-visita/arrastrar-tickets-no-preventivos`, {
      preventivoId,
      tecnicoId,
      fechaVisitaPreventivo
    });
  }

  // CR-202609-0004 - Consulta al backend si existe servicio preventivo para un local
  verificarPreventivoExistente(localId: number): Observable<{ existe: boolean }> {
    return this.http.get<{ existe: boolean }>(`${this.apiUrl}/solicitar-visita/verificar-preventivo/${localId}`);
  }

  // ==========================================================================
  // 3. OPERACIONES CRUD Y CREACIÓN DE TICKETS
  // ==========================================================================

  /** Crea una nueva solicitud de visita (Ticket) */
  crearSolicitudVisita(solicitud: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/solicitar-visita`, solicitud);
  }

  /** Obtiene el detalle completo de una solicitud por su ID */
  getSolicitudVisita(id: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/solicitar-visita/${id}`);
  }

  /** Actualiza los datos generales de una solicitud existente */
  updateSolicitudVisita(id: number, data: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/solicitar-visita/${id}`, data);
  }

  /** Realiza el borrado (lógico o físico) de una solicitud */
  deleteSolicitud(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/solicitar-visita/${id}`);
  }

  /** Carga un lote masivo de solicitudes desde un archivo Excel/CSV */
  subirCargaMasiva(datos: any[]): Observable<any> {
    return this.http.post(`${this.apiUrl}/solicitar-visita/subir-carga-masiva`, datos);
  }

  // ==========================================================================
  // 4. ASIGNACIÓN DE TÉCNICOS Y GESTIÓN EN TERRENO
  // ==========================================================================

  /** Asigna un técnico primario o secundario a una solicitud */
  asignarTecnico(solicitudId: number, tecnicoId: number, tipo: 'tecnico' | 'tecnico_2'): Observable<any> {
    return this.http.post(`${this.apiUrl}/solicitar-visita/${solicitudId}/asignar-tecnico`, { tecnicoId, tipo });
  }

  /** Cambia un técnico previamente asignado */
  cambiarTecnico(solicitudId: number, tecnicoId: number, tipo: 'tecnico' | 'tecnico_2'): Observable<any> {
    return this.http.post(`${this.apiUrl}/solicitar-visita/${solicitudId}/cambiar-tecnico`, { tecnicoId, tipo });
  }

  /** Obtiene la lista de todos los técnicos disponibles en el sistema */
  getTecnicos(): Observable<any> {
    return this.http.get(`${this.apiUrl}/users/tecnicos`);
  }

  // ==========================================================================
  // 5. CHECKLISTS Y REPUESTOS
  // ==========================================================================

  /** Obtiene el checklist de clima u operaciones asociado a una visita */
  getSolicitudVisitaChecklist(id: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/solicitar-visita/${id}/response-checklist`);
  }

  /** Actualiza las respuestas del checklist de una visita */
  updateChecklistVisita(id: number, data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/solicitar-visita/update-checklist-visita/${id}`, data);
  }

  /** Obtiene la información de un repuesto específico por su ID */
  getRepuestosById(id: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/repuestos/${id}`);
  }

  /** Actualiza los repuestos utilizados en una solicitud específica */
  updateRepuestos(id: string, changes: any): Observable<any> {
    return this.http.patch(`${this.apiUrl}/solicitar-visita/${id}/repuestos`, changes);
  }

  // ==========================================================================
  // 6. UTILIDADES, REPORTES Y BÚSQUEDAS
  // ==========================================================================

  /** Obtiene el conteo total de solicitudes pendientes, filtrando opcionalmente por cliente */
  getPendingRequestsCount(clientId?: number): Observable<number> {
    let params = new HttpParams();
    const user = this.authService.currentUserValue;
    
    if (user?.id) params = params.set('userId', user.id.toString());
    if (clientId) params = params.set('clientId', clientId.toString());
    
    return this.http.get<{ count: number }>(`${this.apiUrl}/solicitar-visita/pendientes/count`, { params })
      .pipe(map((response: any) => response.count));
  }

  /** Obtiene las solicitudes programadas para el día de hoy, agrupadas por un cliente específico */
  getSolicitudesDelDiaPorCliente(clientId: number): Observable<any> {
    let params = new HttpParams();
    const user = this.authService.currentUserValue;
    
    if (user?.id) params = params.set('userId', user.id.toString());
    
    return this.http.get(`${this.apiUrl}/solicitar-visita/solicitudes-del-dia/${clientId}`, { params });
  }

  /** Búsqueda global de solicitudes mediante un arreglo de palabras clave */
  buscarSolicitud(searchParams: string[]): Observable<any> {
    const parametros = searchParams.filter(p => p && p.trim()).join(',');
    if (!parametros) return of([]);
    return this.http.get<any>(`${this.apiUrl}/solicitar-visita/buscar/${parametros}`);
  }

  /** Genera y descarga el documento PDF filtrando equipos si es necesario */
  downloadPdf(id: number, activosIds?: number[]): Observable<Blob> {
    let params = new HttpParams();
    
    // Si vienen IDs, los adjuntamos a la URL como ?activos=1,2,3
    if (activosIds && activosIds.length > 0) {
      params = params.set('activos', activosIds.join(','));
    }

    return this.http.get(`${this.apiUrl}/solicitar-visita/${id}/pdf`, { params, responseType: 'blob' });
  }

  /** Dispara el envío manual del correo electrónico con el acta de la visita */
  enviarEmail(id: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/solicitar-visita/${id}/enviar-email`, {});
  }

  // Método genérico unificado apuntando al endpoint multifiltro
  descargarExcelSolicitudes(options: PagedRequestOptions): Observable<any> {
    const params = this.buildHttpParams(options);
    // Retorna el JSON sin paginación, aislando esta responsabilidad de las bandejas normales
    return this.http.get<any>(`${this.apiUrl}/solicitar-visita/solicitudes-visita-multifiltro`, { params });
  }

  exportarExcelMaestro(data: any[], nombreArchivo: string = 'Reporte_Maestro') {
    if (!data || data.length === 0) {
      Swal.fire('Sin datos', 'No hay registros para exportar.', 'warning');
      return;
    }

    // FIX: Detectar si el Backend ya entregó las filas pre-mapeadas con encabezados en español
    const yaEstaMapeado = data[0] && ('Requerimiento' in data[0] || 'Cliente' in data[0]);

    const datosParaExportar = yaEstaMapeado 
      ? data 
      : data.map((sol: any) => ({
          'Requerimiento': sol.id || '',
          'Cliente': sol.client?.nombre || sol.cliente || '',
          'Mes Facturación': sol.facturacion?.mes || sol.mes_facturacion || '',
          'Nombre Local': sol.local?.nombre_local || sol.nombre_local || '',
          'Dirección': sol.local?.direccion || sol.direccion || '',
          'Zona': sol.zona || sol.local?.zona || '',
          'Región': sol.region_nombre || sol.local?.region?.region_nombre || '',
          'Provincia': sol.provincia_nombre || sol.local?.provincia?.provincia_nombre || '',
          'Comuna': sol.comuna_nombre || sol.local?.comuna?.comuna_nombre || '',
          'Encargado Local': sol.local?.nombre_encargado || '',
          'Solicitante': sol.generada_por ? `${sol.generada_por.name} ${sol.generada_por.lastName || ''}`.trim() : '',
          
          // FIX: [CR-202608-0017] Formateo correcto en Excel UTC sin desfase de horas
          'Fecha Ingreso': sol.fechaIngreso ? new Date(sol.fechaIngreso).toLocaleString('es-CL', { timeZone: 'UTC' }) : '',
          
          'Observaciones': sol.observaciones || '',
          'Tipo Servicio': sol.tipoServicio?.nombre || sol.tipo_servicio?.nombre || '',
          'Especialidad': sol.especialidad_nombre || '',
          'Sector Trabajo': sol.sector_trabajo?.nombre || sol.sector_trabajo_default?.nombre || '',
          // FIX: Formato de Fecha Inicio Ticket y Hora Término alineado con reporte de validadas
          'Fecha Inicio Ticket': sol.fecha_hora_inicio_servicio ? new Date(sol.fecha_hora_inicio_servicio).toLocaleString('es-CL', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '',
          'Hora Término': sol.fecha_hora_fin_servicio ? new Date(sol.fecha_hora_fin_servicio).toLocaleString('es-CL', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '',
          
          // FEAT: [CR-202608-0017] Cálculo de tiempo de actividad homologado con HH por técnico (hh:mm:ss / Xh Ym)
          'Tiempo de Actividad': (() => {
            if (sol.fecha_hora_inicio_servicio && sol.fecha_hora_fin_servicio) {
              const diffMs = new Date(sol.fecha_hora_fin_servicio).getTime() - new Date(sol.fecha_hora_inicio_servicio).getTime();
              if (diffMs > 0) {
                const horas = Math.floor(diffMs / (1000 * 60 * 60));
                const minutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}:00`;
              }
            }
            return '00:00:00';
          })(),
          'Validado por': sol.validada_por ? `${sol.validada_por.name} ${sol.validada_por.lastName || ''}`.trim() : '',
          'Fecha Validación': sol.fecha_hora_validacion ? new Date(sol.fecha_hora_validacion).toLocaleString('es-CL') : '',
          'Causa Raíz': sol.causaRaiz?.nombre || '',
          'Garantía': sol.garantia || '',
          'Turno': sol.turno || '',
          'Estado Solicitud': sol.estado_solicitud || sol.status || '',
          'Tipo Solicitud': sol.tipo_solicitud?.nombre || sol.tipoSolicitud?.nombre || '',
          'Técnico Asignado': sol.tecnico_asignado ? `${sol.tecnico_asignado.name} ${sol.tecnico_asignado.lastName || ''}`.trim() : '',
          'Segundo Auxiliar': sol.tecnico_asignado_2 ? `${sol.tecnico_asignado_2.name} ${sol.tecnico_asignado_2.lastName || ''}`.trim() : '',
          'SLA Hora (Configurado)': sol.sla_horas_permitidas || 0,
          'Horas Respuesta (Real)': sol.horas_respuesta ?? '',
          'Cumplimiento SLA': sol.cumplimiento_sla || '',
          'Estado Flujo': sol.status || '',
          'Valor Total Repuestos': sol.valor_total_repuestos || 0,
          'Valor Total Servicio': Number(sol.valorPorLocal || 0)
        }));

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(datosParaExportar);

    // FIX: Configurar anchos de columna tras remover 'Hora Inicio' y extender 'Hora Término'
    ws['!cols'] = [
      { wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: 25 }, { wch: 30 },
      { wch: 12 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 },
      { wch: 25 }, { wch: 18 }, { wch: 45 }, { wch: 18 }, { wch: 20 },
      { wch: 18 }, { wch: 22 }, { wch: 22 }, { wch: 20 }, { wch: 20 },
      { wch: 20 }, { wch: 18 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, 
      { wch: 15 }, { wch: 18 }, { wch: 25 }, { wch: 25 }, { wch: 12 }, 
      { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 18 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Listado_Dinamico');
    const fechaStr = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `${nombreArchivo}_${fechaStr}.xlsx`);
    Swal.close();
  }

  // ==========================================================================
  // 7. MÉTODOS PRIVADOS (HELPER)
  // ==========================================================================

  /**
   * Método interno para invocar el backend unificando la construcción de parámetros.
   */
  private fetchPaginatedData(endpoint: string, options: PagedRequestOptions): Observable<any> {
    const params = this.buildHttpParams(options);
    return this.http.get<any>(`${this.apiUrl}/solicitar-visita${endpoint}`, { params });
  }

  /**
   * Construye dinámicamente los HttpParams en base a la interfaz PagedRequestOptions.
   * Inyecta automáticamente el ID del usuario actual por seguridad (RBAC).
   */
  private buildHttpParams(options: PagedRequestOptions): HttpParams {
    let params = new HttpParams();

    // Prioriza el userId de las options, si no, lo saca del AuthService
    const user = this.authService.currentUserValue;
    const userId = options.userId || user?.id;
    if (userId) {
      params = params.set('userId', userId.toString());
    }

    if (options.export) {
      params = params.set('export', 'true');
    } else {
      if (options.page !== undefined) params = params.set('page', options.page.toString());
      if (options.limit !== undefined) params = params.set('limit', options.limit.toString());
    }
    
    if (options.clientId) params = params.set('clientId', options.clientId.toString());
    if (options.searchTerm?.trim()) params = params.set('searchTerm', options.searchTerm.trim());
    if (options.serviceType?.trim()) params = params.set('serviceType', options.serviceType.trim());
    if (options.tipoSolicitud?.trim()) params = params.set('tipoSolicitud', options.tipoSolicitud.trim());
    if (options.sortBy) params = params.set('sortBy', options.sortBy);
    if (options.sortOrder) params = params.set('sortOrder', options.sortOrder);
    if (options.fechaInicio) params = params.set('fechaInicio', options.fechaInicio);
    if (options.fechaFin) params = params.set('fechaFin', options.fechaFin);
    if (options.localIds) params = params.set('localIds', options.localIds);
    if (options.activoFijoId) params = params.set('activoFijoId', options.activoFijoId.toString());
    if (options.mesFacturacion) params = params.set('mesFacturacion', options.mesFacturacion);
    
    if (options.dias !== undefined && options.dias !== null) {
      params = params.set('dias', options.dias.toString());
    }
    if (options.status?.trim()) params = params.set('status', options.status.trim());
    if (options.tipoMantenimiento?.trim()) params = params.set('tipoMantenimiento', options.tipoMantenimiento.trim());
    if (options.kpiType?.trim()) params = params.set('kpiType', options.kpiType.trim());
    if (options.statusTarget?.trim()) params = params.set('statusTarget', options.statusTarget.trim());
    if (options.cumplimientoType?.trim()) {
      params = params.set('cumplimientoType', options.cumplimientoType.trim());
    }
    if (options.dias !== undefined && options.dias !== null) params = params.set('dias', options.dias.toString());
    return params;
  }

  getGeneralListadoDinamico(options: PagedRequestOptions): Observable<any> {
    const params = this.buildHttpParams(options);
    return this.http.get(`${this.apiUrl}/solicitar-visita/listado-general-dinamico`, { params });
  }

}